import React, { useEffect, useState, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Button, Row, Col } from "reactstrap";
import { toast } from "react-toastify";
import Swal from "sweetalert2";
import {
  getFormattedPrice,
  getAPIURL,
  getVendorID,
} from "../../../services/helper";
import {
  cartPaid,
  backToCart,
  setPayType,
  setCustomer,
  setCreditCardSaleData,
} from "../../../ReduxToolkit/Reducers/CartReducer";
import { closeView } from "../../../ReduxToolkit/Reducers/viewReducer";
import http from "../../../services/http";
import shortcutManager from "../../../services/shortcutManager";
import PaymentMethod from "./PaymentMethod";
import PaymentSummary from "./PaymentSummary";
import "./paycart.css";

const PayCart = () => {
  const dispatch = useDispatch();
  const { total, order_type } = useSelector((state) => state.cart);
  const { paymentTypes } = useSelector((state) => state.app);

  const [payments, setPayments] = useState([]);
  const [remainingBalance, setRemainingBalance] = useState(parseFloat(total));
  const [paymentAmounts, setPaymentAmounts] = useState({});
  const [currentInputIndex, setCurrentInputIndex] = useState(0);
  const firstInputRef = useRef(null);

  const api_url = getAPIURL();
  const vendor_id = getVendorID();

  const paymentMethods = [
    { id: 3333, name: "Cash", color: "success" },
    { id: 4444, name: "Card", color: "primary" },
    { id: 1111, name: "PIN", color: "info" },
    { id: 2222, name: "EBT", color: "warning" },
    ...(paymentTypes || []).map((pt) => ({
      id: pt.id,
      name: pt.title,
      color: "secondary",
    })),
  ];

  useEffect(() => {
    setRemainingBalance(
      parseFloat(total) - payments.reduce((sum, p) => sum + p.amount, 0)
    );
  }, [payments, total]);

  useEffect(() => {
    const timer = setTimeout(() => {
      if (firstInputRef.current) {
        firstInputRef.current.focus();
        firstInputRef.current.select();
      }
    }, 500);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    if (remainingBalance <= 0 && payments.length > 0) {
      setTimeout(() => {
        handleCompleteTransaction();
      }, 300);
    }
  }, [remainingBalance, payments.length]);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.altKey && e.key === "b") {
        e.preventDefault();
        handleBack();
      }
      if (e.altKey && e.key === "x" && payments.length > 0) {
        e.preventDefault();
        handleCompleteTransaction();
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [payments]);

  const handleBack = () => {
    dispatch(backToCart());
    dispatch(closeView());
  };

  const canPay = (methodId) => parseFloat(paymentAmounts[methodId] || 0) > 0;

  const handleCompleteTransaction = async () => {
    if (payments.length === 0) return;

    const totalTendered = payments.reduce(
      (sum, p) => sum + parseFloat(p.amount),
      0
    );
    const orderTotal = parseFloat(total);

    if (remainingBalance <= 0) {
      const result = await Swal.fire({
        title: "Payment Complete",
        html: `<p>Total: ${getFormattedPrice(
          orderTotal
        )}<br>Tendered: ${getFormattedPrice(
          totalTendered
        )}<br>Change: ${getFormattedPrice(
          Math.max(0, totalTendered - orderTotal)
        )}</p>`,
        icon: "success",
        confirmButtonText: "OK",
      });

      if (result.isConfirmed) {
        dispatch(
          cartPaid({
            splitPayment: { payments, totalPaid: orderTotal },
          })
        );
        dispatch(closeView());
      }
    }
  };

  const handleSpecialPayment = async (payTypeID) => {
    if (payTypeID === 1111) {
      const { value } = await Swal.fire({
        title: "Customer PIN",
        html: `<input type="password" id="customer_id" class="swal2-input" placeholder="Customer ID">
               <input type="password" id="pin" class="swal2-input" placeholder="4-digit PIN" maxlength="4">`,
        showCancelButton: true,
        preConfirm: () => {
          const customerId =
            Swal.getPopup().querySelector("#customer_id").value;
          const pin = Swal.getPopup().querySelector("#pin").value;
          if (!customerId || !pin || pin.length !== 4) {
            Swal.showValidationMessage(
              "Enter valid Customer ID and 4-digit PIN"
            );
            return false;
          }
          return { customerId, pin };
        },
      });

      if (value) {
        try {
          const { data } = await http.post(`${api_url}/customers/pay-by-pin`, {
            customer_unique_id: value.customerId,
            pin: value.pin,
            order_total: total,
            vendor_id,
          });
          if (data.success) {
            const { customer_id, ...cc_sale_data } = data.data;
            dispatch(setCustomer(customer_id));
            dispatch(setCreditCardSaleData(cc_sale_data));
            dispatch(cartPaid());
            toast.success("Payment successful!");
          }
        } catch (error) {
          toast.error("Payment failed");
        }
      }
    } else if (payTypeID === 2222) {
      const { value } = await Swal.fire({
        title: "EBT Card",
        input: "text",
        inputPlaceholder: "Enter EBT Card Number",
        showCancelButton: true,
      });

      if (value) {
        try {
          const { data } = await http.post(`${api_url}/customers/pay-by-ebt`, {
            ebt_card: value,
            order_total: total,
            vendor_id,
          });
          if (data.success) {
            const { redirect_url, accu_id } = data.data;
            const form = document.createElement("form");
            form.action = redirect_url;
            form.method = "post";
            form.innerHTML = `<input type="hidden" name="AccuId" value="${accu_id}">
                             <input type="hidden" name="AccuReturnURL" value="${api_url}/snap-ebt-return">
                             <input type="hidden" name="AccuLanguage" value="en-US">`;
            document.body.appendChild(form);
            form.submit();
          }
        } catch (error) {
          toast.error("EBT payment failed");
        }
      }
    }
  };

  const handlePaymentTypeSelect = (payTypeID) => {
    dispatch(setPayType(payTypeID));
    const amount = parseFloat(paymentAmounts[payTypeID] || 0);

    if (payTypeID === 1111 || payTypeID === 2222) {
      handleSpecialPayment(payTypeID);
      return;
    }

    if (canPay(payTypeID)) {
      const paymentMethod = paymentMethods.find((m) => m.id === payTypeID);
      const newPayment = {
        id: Date.now(),
        payTypeID,
        amount: amount,
        type: paymentMethod?.name || "Unknown",
      };

      const updatedPayments = [...payments, newPayment];
      setPayments(updatedPayments);
      setPaymentAmounts((prev) => ({ ...prev, [payTypeID]: "" }));

      // Move to next input
      const nextIndex = (currentInputIndex + 1) % paymentMethods.length;
      setCurrentInputIndex(nextIndex);
      setTimeout(() => {
        const nextInput = document.querySelector(
          `input[data-payment-index="${nextIndex}"]`
        );
        if (nextInput) nextInput.focus();
      }, 100);
    }
  };

  return (
    <div
      className="pay-cart-section"
      style={{ maxHeight: "90vh", overflow: "hidden" }}
    >
      <Row>
        <Col md={12}>
          <PaymentSummary
            total={total}
            remainingBalance={remainingBalance}
            payments={payments}
          />

          <div
            className="payment-methods"
            style={{ maxHeight: "65vh", overflow: "hidden" }}
          >
            <Row>
              {paymentMethods.map((method, index) => (
                <Col md={6} key={method.id} className="mb-1">
                  <PaymentMethod
                    method={method}
                    index={index}
                    amount={paymentAmounts[method.id] || ""}
                    onChange={(e) =>
                      setPaymentAmounts((prev) => ({
                        ...prev,
                        [method.id]: e.target.value,
                      }))
                    }
                    onPay={() => handlePaymentTypeSelect(method.id)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" && canPay(method.id)) {
                        handlePaymentTypeSelect(method.id);
                      }
                    }}
                    canPay={canPay(method.id)}
                    remainingBalance={remainingBalance}
                    inputRef={index === 0 ? firstInputRef : null}
                  />
                </Col>
              ))}
            </Row>
          </div>

          <div className="mt-3">
            {payments.length > 0 && (
              <Button
                color="success"
                onClick={handleCompleteTransaction}
                className="w-100 mb-2"
                size="lg"
                style={{ fontSize: "14px", fontWeight: "bold" }}
              >
                Complete Transaction (Alt+X)
              </Button>
            )}
            <Button
              color="warning"
              onClick={handleBack}
              className="w-100"
              size="md"
              style={{ fontSize: "12px" }}
            >
              Back to Cart (Alt+B)
            </Button>
          </div>
        </Col>
      </Row>
    </div>
  );
};

export default PayCart;
